import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AuthUser } from '@crm/shared';
import { api, refreshAccessToken, tokenStore } from '../api/client';
import { queryClient } from '../app/query-client';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: Status;
  user: AuthUser | null;
  needsSetup: boolean | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  acceptInvite: (token: string, name: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSetupStatus: () => Promise<boolean>;
  /** Applies a profile change (name, photo) the user just saved, so the header follows at once. */
  updateUser: (patch: Partial<Pick<AuthUser, 'name' | 'photoFileId'>>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthResponse {
  accessToken: string;
  user: { sub: string; email: string; name: string; role: AuthUser['role']; photoFileId: string | null };
}

const toUser = (u: AuthResponse['user']): AuthUser => ({ id: u.sub, email: u.email, name: u.name, role: u.role, photoFileId: u.photoFileId ?? null });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);
  // someone was signed in on this tab, so the cache may hold their data
  const hadSession = useRef(false);

  const applyAuth = useCallback((data: AuthResponse) => {
    hadSession.current = true;
    tokenStore.set(data.accessToken);
    setUser(toUser(data.user));
    setStatus('authenticated');
  }, []);

  const clear = useCallback(() => {
    tokenStore.set(null);
    // drop cached data so the next person to sign in on this tab never sees it. Only after a real session: clearing
    // on a signed-out first load also drops in-flight public queries (a hosted form then never finishes loading)
    if (hadSession.current) queryClient.clear();
    hadSession.current = false;
    setUser(null);
    setStatus('anonymous');
  }, []);

  const refreshSetupStatus = useCallback(async () => {
    const r = await api.get<{ needsSetup: boolean }>('/auth/status');
    setNeedsSetup(r.data.needsSetup);
    return r.data.needsSetup;
  }, []);

  useEffect(() => {
    tokenStore.setUnauthorizedHandler(clear);
    (async () => {
      const token = await refreshAccessToken();
      if (token) {
        try {
          const me = await api.get<AuthUser>('/auth/me');
          hadSession.current = true;
          setUser({ ...me.data, photoFileId: me.data.photoFileId ?? null });
          setStatus('authenticated');
        } catch {
          clear();
        }
      } else {
        clear();
        void refreshSetupStatus().catch(() => setNeedsSetup(false));
      }
    })();
    return () => tokenStore.setUnauthorizedHandler(null);
  }, [clear, refreshSetupStatus]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      needsSetup,
      refreshSetupStatus,
      login: async (email, password) => {
        const r = await api.post<AuthResponse>('/auth/login', { email, password });
        applyAuth(r.data);
      },
      register: async (name, email, password) => {
        const r = await api.post<AuthResponse>('/auth/register', { name, email, password });
        setNeedsSetup(false);
        applyAuth(r.data);
      },
      acceptInvite: async (token, name, password) => {
        const r = await api.post<AuthResponse>('/auth/accept-invite', { token, name, password });
        applyAuth(r.data);
      },
      logout: async () => {
        try {
          await api.post('/auth/logout');
        } finally {
          clear();
        }
      },
      updateUser: (patch) => setUser((u) => (u ? { ...u, ...patch } : u)),
    }),
    [status, user, needsSetup, applyAuth, clear, refreshSetupStatus],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
